import {Component, inject, Input, OnDestroy} from '@angular/core';
import {MatCardModule} from "@angular/material/card";
import {AgGridAngular} from "ag-grid-angular";
import {ColDef, GridApi, GridReadyEvent, RowClickedEvent} from "ag-grid-community";
import {IGamePlayerInputData} from "../../../interfaces/player/IGamePlayerInputData";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {PlayersService} from "../../../services/players.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {PlayerInfoTableDefs} from "../../player-game-info/player-info-table-defs";
import {concatMap, forkJoin, Subscription, tap} from "rxjs";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {IPurchasedFiftyFiftyStrips} from "../../../interfaces/player/IPurchasedFiftyFiftyStrips";
import {GameInstanceService} from "../../../services/game-instance.service";
import {IActiveGameInstance} from "../../../interfaces/IActiveGameInstance";
import {ActiveUserService} from "../../../services/active-user.service";
import {
  IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult
} from "../../../interfaces/number/IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult";
import {switchMap} from "rxjs/operators";
import {IFailedAutoplayStripsRow} from "../../../interfaces/player/IFailedAutoplayStripsRow";
import {IAutoplayGroupWithPaymentTier} from "../../../interfaces/IAutoplayGroupWithPaymentTier";
import {
  IFiftyFiftyFailedAutoplayStripWithDetails
} from "../../../interfaces/player/IFiftyFiftyFailedAutoplayStripWithDetails";
import {MatDialog} from "@angular/material/dialog";
import {FailedAutoplayDetailsComponent} from "../../dialogs/failed-autoplay-details/failed-autoplay-details.component";

@Component({
  selector: 'app-manage-player-fifty-fifty-numbers',
  standalone: true,
  imports: [
    MatCardModule,
    AgGridAngular,
  ],
  templateUrl: './manage-player-fifty-fifty-numbers.component.html',
  styleUrl: './manage-player-fifty-fifty-numbers.component.css'
})
export class ManagePlayerFiftyFiftyNumbersComponent implements OnDestroy {

  private playerService: PlayersService = inject(PlayersService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private playerInfoTables: PlayerInfoTableDefs = inject(PlayerInfoTableDefs);
  private gameInstanceService: GameInstanceService = inject(GameInstanceService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private matDialog: MatDialog = inject(MatDialog);

  @Input()
  public set gamePlayerData(gamePlayerDataP: IGamePlayerInputData) {
    if (gamePlayerDataP.playerId && gamePlayerDataP.game.Id) {
      this.unsubscribeFromFetchingNumbers();
      this.clearGameData();
      this.game = gamePlayerDataP.game;
      this.playerId = gamePlayerDataP.playerId;
      this.setupColumnDefs();
      this.setupFetchNumbers();
    }
  }

  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateForTemplate = UIStateEnum;

  public failedAutoplayStrips: IFailedAutoplayStripsRow[] = [];
  public failedAutoplayStripsRawData: IFiftyFiftyFailedAutoplayStripWithDetails[] = [];
  public playedStripRows: IPurchasedFiftyFiftyStrips[] = [];
  public autoplayGroups: IAutoplayGroupWithPaymentTier[] = [];

  public game: IGameQueryResult | null = null;
  public activeGameInstance: IActiveGameInstance | null = null;
  public playerId: string = '';

  public currentDrawGridApi!: GridApi<IPurchasedFiftyFiftyStrips>;
  public autoplayGroupsGridApi!: GridApi<IAutoplayGroupWithPaymentTier>;
  public failedAutoplayGridApi!: GridApi<IFailedAutoplayStripsRow>;

  public currentDrawColumnDefs: ColDef[] = [];
  public autoplayGroupsColumnDefs: ColDef[] = [];
  public failedAutoplayColumnDefs: ColDef[] = [];

  private fetchNumbersSubscription: Subscription | null = null;

  ngOnDestroy(): void {
    this.unsubscribeFromFetchingNumbers();
  }

  private unsubscribeFromFetchingNumbers() {
    if (this.fetchNumbersSubscription) {
      this.fetchNumbersSubscription.unsubscribe();
    }
  }

  private setupColumnDefs() {
    this.currentDrawColumnDefs = this.playerInfoTables.getColumnDefsForGameType(this.game!);
    this.autoplayGroupsColumnDefs = this.playerInfoTables.autoplayGroupsColumnDefs;
    this.failedAutoplayColumnDefs = this.playerInfoTables.failedAutoplayStripsColumnDefs;
  }

  private setupFetchNumbers() {
    this.fetchNumbersSubscription = this.playerService.fetchUpdatedTicketNumbersSubject$
      .pipe(
        concatMap(() => {
          this.uiState = UIStateEnum.ShowLoading;

          return this.gameInstanceService.activeGameInstanceByGame(this.game?.Id!, this.activeUserService.activeUser().Id)
            .pipe(
              switchMap((activeGameInstance) => {
                this.activeGameInstance = activeGameInstance;

                return forkJoin({
                  playerNumbers: this.playerService.getFiftyFiftyPlayerNumbersByGameInstance(this.playerId, activeGameInstance?.Id!),
                  autoplayGroups: this.playerService.getAutoplayGroupsForPlayerInGame(this.playerId, this.game?.Id!)
                }).pipe(
                  tap(data => {
                    this.setFiftyFiftyNumbers(data.playerNumbers);
                    this.autoplayGroups = data.autoplayGroups;
                  })
                );
              })
            );
        })
      ).subscribe({
        error: (err) => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayPageLevelErrorMessage(err, 'There was an issue retrieving Fifty Fifty numbers. Please try reloading.');
        }
      });
  }

  private setFiftyFiftyNumbers(numberData: IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult) {
    this.playedStripRows = numberData.PlayedFiftyFiftyStrips;
    this.failedAutoplayStripsRawData = numberData.FailedAutoplayStrips;
    this.failedAutoplayStrips = this.formatFailedAutoplayFiftyFiftyStrips(numberData.FailedAutoplayStrips);
    this.uiState = UIStateEnum.ShowData;
  }

  private formatFailedAutoplayFiftyFiftyStrips(strips: IPurchasedFiftyFiftyStrips[]): IFailedAutoplayStripsRow[] {
    let rows: IFailedAutoplayStripsRow[] = [];

    if (strips && strips.length > 0) {
      strips.forEach(strip => {
        rows.push({
          Id: strip.Id,
          TicketNumbers: strip.TicketNumbers.join(", "),
          PaymentTierString: strip.PaymentTierId
        });
      });
    }

    return rows;
  }

  public onCurrentDrawGridReady(params: GridReadyEvent<IPurchasedFiftyFiftyStrips>) {
    this.currentDrawGridApi = params.api;
    this.currentDrawGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onAutoplayGroupsGridReady(params: GridReadyEvent<IAutoplayGroupWithPaymentTier>) {
    this.autoplayGroupsGridApi = params.api;
    this.autoplayGroupsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onFailedAutoplayGridReady(params: GridReadyEvent<IFailedAutoplayStripsRow>) {
    this.failedAutoplayGridApi = params.api;
    this.failedAutoplayGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onFailedAutoplayStripRowClick(event: RowClickedEvent<IFailedAutoplayStripsRow>) {
    const clickedStrip = event.data;
    // Find the corresponding raw data that has the AutoplayFailureDetails
    const rawStrip = this.failedAutoplayStripsRawData.find(strip => strip.Id === clickedStrip?.Id);

    if (rawStrip && clickedStrip) {
      this.matDialog.open(FailedAutoplayDetailsComponent, {
        width: '650px',
        data: {
          strip: clickedStrip,
          autoplayFailureDetails: rawStrip.AutoplayFailureDetails
        }
      });
    }
  }

  private clearGameData() {
    this.playedStripRows = [];
    this.failedAutoplayStrips = [];
    this.failedAutoplayStripsRawData = [];
    this.autoplayGroups = [];
    this.uiState = UIStateEnum.ShowLoading;
  }
}
